// Live preview of the customer-facing loyalty surfaces, driven by the
// admin's draft "card appearance" values. Two mockups side by side:
//
//   1. The scanned screen — a faithful replica of the server-rendered
//      walletLandingPage (wallet_enroll.go): logo, headline, big progress,
//      stamp dots, sub copy, and the two "Add to Wallet" buttons.
//   2. The wallet pass — a compact card tinted with the accent colour,
//      matching how Google (hexBackgroundColor) and Apple (backgroundColor)
//      render the pass.
//
// Keep the structure aligned with walletLandingPage so the preview doesn't
// drift from what actually renders. All values are already merged (override
// → receipt fallback) by the caller.

// ── palette helpers (mirror backend/internal/wallet/palette.go so the mockup
//    matches the real Apple/Google pass) ───────────────────────────────────
function clampHex(s: string): string {
  const m = (s || '').trim().replace(/^#/, '')
  if (m.length === 3)
    return '#' + m.split('').map((c) => c + c).join('')
  if (m.length === 6) return '#' + m
  return '#d97706'
}
function hexToRgb(hex: string): [number, number, number] {
  const h = clampHex(hex).slice(1)
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}
function toHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`
}
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a)
  const [br, bg, bb] = hexToRgb(b)
  return toHex(ar * (1 - t) + br * t, ag * (1 - t) + bg * t, ab * (1 - t) + bb * t)
}
// ensureBright lifts very dark accents so the gold reads on the dark card.
function ensureBright(hex: string): string {
  const [r, g, b] = hexToRgb(hex)
  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  if (lum >= 120) return clampHex(hex)
  return mix(hex, '#ffe6b0', Math.min(0.5, (120 - lum) / 255))
}
// A fixed faux-QR pattern (deterministic; no randomness).
const QR_PATTERN = [
  1, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 1, 1,
]

interface LoyaltyScreenPreviewProps {
  accent: string // a valid 6-digit hex; caller normalises
  /** The wallet-pass mark (mockup 2). */
  logoUrl?: string
  /** The web-page logo (mockup 1). Omitted = falls back to logoUrl, which
   *  mirrors the server's mergeLoyaltyBranding precedence. */
  pageLogoUrl?: string
  /** Banner drawn behind the stamp coins. Mirrors PassState.HeroURL. */
  heroUrl?: string
  headline: string
  subtext?: string
  businessName: string
  balance: number
  threshold: number
  rewardLabel: string
}

export function LoyaltyScreenPreview({
  accent,
  logoUrl,
  pageLogoUrl,
  heroUrl,
  headline,
  subtext,
  businessName,
  balance,
  threshold,
  rewardLabel,
}: LoyaltyScreenPreviewProps) {
  // The page mockup uses the page logo; the pass mockup always uses logoUrl.
  const pageLogo = pageLogoUrl || logoUrl
  const safeThreshold = Math.max(1, threshold)
  const safeBalance = Math.max(0, Math.min(balance, safeThreshold))
  const remaining = safeThreshold - safeBalance

  // Mirrors walletLandingPage's sub-copy logic, overridden by the admin's
  // subtext when set.
  const autoSub =
    remaining > 0
      ? `${remaining} more stamp${remaining === 1 ? '' : 's'} to ${rewardLabel || 'your reward'}`
      : 'Reward unlocked! 🎉'
  const sub = subtext && subtext.trim() ? subtext : autoSub

  // Cap the rendered dots so a large threshold doesn't blow out the frame.
  const dotCount = Math.min(safeThreshold, 12)
  const dots = Array.from({ length: dotCount }, (_, i) => i < safeBalance)

  // Derived pass palette — espresso base + lifted-accent gold, per palette.go.
  const gold = ensureBright(accent)
  const goldHi = mix(gold, '#ffffff', 0.34)
  const espresso = mix('#0b0a07', accent, 0.15)
  const espresso2 = mix('#0b0a07', accent, 0.26)
  const panel = mix(espresso, accent, 0.1)
  const cream = '#f3e9d4'
  const coinCount = Math.min(safeThreshold, 8)
  const coins = Array.from({ length: coinCount }, (_, i) =>
    i < safeBalance ? 'on' : i === safeBalance && remaining > 0 ? 'next' : 'empty',
  )

  return (
    <div className="flex flex-wrap gap-4">
      {/* 1. Scan screen (phone frame) */}
      <div className="w-[190px] shrink-0 rounded-[26px] border-[6px] border-stone-800 bg-stone-800 shadow-md">
        <div className="rounded-[20px] bg-[#faf7f2] px-4 py-5 text-center">
          {pageLogo ? (
            <img src={pageLogo} alt="" className="mx-auto mb-2 max-h-9 max-w-[60%] object-contain" />
          ) : null}
          <div className="truncate text-[14px] font-semibold text-stone-900">{headline || 'Loyalty rewards'}</div>
          <div className="mt-2 text-[30px] font-bold leading-none" style={{ color: accent }}>
            {safeBalance} of {safeThreshold}
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {dots.map((on, i) => (
              <span
                key={i}
                className="h-3 w-3 rounded-full border-2"
                style={{ borderColor: accent, background: on ? accent : 'transparent', opacity: on ? 1 : 0.4 }}
              />
            ))}
          </div>
          <div className="mt-2 min-h-[16px] text-[11px] leading-tight text-stone-500">{sub}</div>
          <div className="mt-3 space-y-1.5">
            <div className="rounded-lg bg-stone-900 py-2 text-[11px] font-semibold text-white"> Add to Apple Wallet</div>
            <div className="rounded-lg border border-stone-900 py-2 text-[11px] font-semibold text-stone-900"> Add to Google Wallet</div>
          </div>
        </div>
      </div>

      {/* 2. Wallet pass mockup — matches the redesigned Apple/Google pass */}
      <div
        className="w-[190px] shrink-0 self-start overflow-hidden rounded-xl shadow-md"
        style={{ background: `linear-gradient(165deg, ${espresso2}, ${espresso})`, color: cream }}
      >
        <div className="flex items-center gap-2 px-3 pt-3">
          <div
            className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-[7px]"
            style={{ background: `linear-gradient(150deg, ${mix(panel, gold, 0.2)}, ${espresso})` }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-full w-full object-contain p-[3px]" />
            ) : (
              <span className="h-2 w-2 rounded-full" style={{ background: gold }} />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[7px] font-bold uppercase tracking-[0.2em]" style={{ color: gold }}>
              Loyalty card
            </div>
            <div className="truncate text-[12px] font-semibold leading-tight">{businessName || 'Loyalty'}</div>
          </div>
          <div className="ml-auto shrink-0 text-right">
            <div className="text-[7px] font-bold uppercase tracking-[0.15em]" style={{ color: gold }}>
              Stamps
            </div>
            <div className="text-[12px] font-bold leading-none">
              {safeBalance}/{safeThreshold}
            </div>
          </div>
        </div>

        {/* strip band of stamp coins — over the tenant banner when set. The
            0.47 black scrim matches heroScrimAlpha (120/255) in passimage.go;
            keep the two in step or this stops predicting the real pass. */}
        <div
          className="mx-2 mt-2 rounded-lg px-2.5 py-2.5"
          style={
            heroUrl
              ? {
                  backgroundImage: `linear-gradient(rgba(0,0,0,.47), rgba(0,0,0,.47)), url(${JSON.stringify(heroUrl)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { background: panel }
          }
        >
          <div className="flex flex-wrap gap-1.5">
            {coins.map((s, i) => (
              <span
                key={i}
                className="h-[18px] w-[18px] rounded-full"
                style={
                  s === 'on'
                    ? { background: `radial-gradient(circle at 35% 30%, ${goldHi}, ${gold})`, boxShadow: `0 1px 3px ${gold}66` }
                    : s === 'next'
                      ? { boxShadow: `inset 0 0 0 2px ${gold}` }
                      : { boxShadow: `inset 0 0 0 1.5px ${gold}`, opacity: 0.32 }
                }
              />
            ))}
          </div>
        </div>

        <div className="flex items-end justify-between px-3 pb-1 pt-2">
          <div className="min-w-0">
            <div className="text-[7px] font-bold uppercase tracking-[0.15em]" style={{ color: gold }}>
              Reward
            </div>
            <div className="truncate text-[12px] font-medium">{rewardLabel || 'Reward'}</div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[7px] font-bold uppercase tracking-[0.15em]" style={{ color: gold }}>
              To go
            </div>
            <div className="text-[12px] font-medium">{remaining > 0 ? remaining : '✓'}</div>
          </div>
        </div>

        <div className="flex justify-center pb-3 pt-1.5">
          <div className="grid grid-cols-5 gap-[2px] rounded-md bg-white p-1.5">
            {QR_PATTERN.map((on, i) => (
              <span
                key={i}
                className="h-[5px] w-[5px] rounded-[1px]"
                style={{ background: on ? '#0c0a08' : 'transparent' }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
